import _ from 'lodash'
import config from '@config'

import { ADDRESS_TYPES, TYPE_COLORS } from './constants'

function generateRandomHash(length) {
    let result = ''
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    const charactersLength = characters.length
    for (let i = 0; i < length; i += 1) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength))
    }
    return result
}

/**
 * Returns url of an icon based on address' type and historical parameters
 * @param {object} address Address object
 */
const getIconUrl = (address, isActive) => {
    const baseUrl = 'http://gnosus.bisnode.cz/images/'
    const hash = generateRandomHash(10)
    const iconName = `pin-${_.get(ADDRESS_TYPES, `${_.get(address, 'type', 'default')}`)}${
        _.get(address, 'historical') ? '-historical' : ''
    }${isActive ? '-active' : ''}.png` // eslint-disable-line

    return `${baseUrl}${iconName}?v=${hash}`
}

/**
 * Returns an object containing addresses filtered by their types and additional parameters (i.e.: { historical: true }).
 * @param {array} addresses Array of all address objects
 * @param {object} params Additional parameters used to filter addresses
 * @param {boolean} showRegistered True - returns filtered array for registered addresses, otherwise registered is going to be empty array
 * @param {boolean} showShops True - returns filtered array for shops addresses, otherwise registered is going to be empty array
 * @param {bolean} showBranch True - returns filtered array for branch addresses, otherwise registered is going to be empty array
 */
const getAddressesTypesObj = (addresses, params, showRegistered, showShops, showBranch) => ({
    registered: showRegistered ? _.filter(addresses, { type: 'ADA001', ...params }) : undefined,
    shops: showShops ? _.filter(addresses, { type: 'ADA006', ...params }) : undefined,
    branch: showBranch ? _.filter(addresses, { type: 'ADA0RP', ...params }) : undefined,
})

/**
 * Returns color based on the address' type
 * @see TYPE_COLORS in ./constants
 * @param {object} address Object of an address
 */
const getColor = (address) => _.get(TYPE_COLORS, `${_.get(address, 'type')}`)

/**
 * Returns url query parameter, which contains a marker of the currently selected address.
 * Concat this to the output url to show the marker on the map.
 * @param {object} activeAddress Object of the currently selected address
 */
const getActiveAddressMarker = (activeAddress) => {
    const gpsX = _.get(activeAddress, 'gpsX', null)
    const gpsY = _.get(activeAddress, 'gpsY', null)
    const value = _.get(activeAddress, 'value', null)

    return `markers=size:mid|color:${getColor(activeAddress)}|scale:2|icon:${getIconUrl(activeAddress, true)}|${
        !gpsX || !gpsY ? `${value}` : `${gpsX},${gpsY}`
    }` // eslint-disable-line
}

/**
 * Returns array of object, which contain marker settings and locations.
 * Output is used in addMarkersToUrl function to assemble the google maps url
 * @see addMarkersToUrl()
 * @param {array} addresses Array of all address objects to show on map
 * @param {object} activeAddress Object of the currently selected address
 * @param {boolean} historical If true returns historical markers
 * @param {boolean} showRegistered If true output will contain registered markers
 * @param {boolean} showBranch If true output will contain branch markers
 * @param {boolean} showShops If true output will contain shop markers
 */
const getMarkers = (addresses, activeAddress, historical = false, showRegistered, showBranch, showShops) => {
    const addressesType = getAddressesTypesObj(addresses, { historical }, showRegistered, showBranch, showShops)

    return _.reduce(
        addressesType,
        (acc, addressTypeObject) => {
            if (_.isEmpty(addressTypeObject)) return acc

            const firstAddress = _.get(addressTypeObject, '[0]', {})

            const locations = addressTypeObject
                .filter((x) => _.get(x, 'id') !== _.get(activeAddress, 'id')) // Filter out active address
                .map((x) => _.get(x, 'gpsX') && _.get(x, 'gpsY') && `${x.gpsX.toFixed(4)},${x.gpsY.toFixed(4)}`) // Get GPS coords
                .filter((x) => !_.isNil(x)) // Filter out addresses with no gps coords

            if (_.isEmpty(locations)) return acc

            return [
                ...acc,
                {
                    color: getColor(firstAddress),
                    size: historical ? 'tiny' : 'small',
                    scale: historical ? 2 : undefined,
                    icon: historical ? getIconUrl(firstAddress, false) : undefined,
                    locations,
                },
            ]
        },
        []
    )
}

/**
 * Adds as many markers to the supplied url as possible
 * Maximum url length is 8192 characters (conditions are using 8000 chars to leave a bit of space just in case for api keys etc.)
 * When the maximum url length is reached, no more markers are going to be added to the url and will not be shown on the map
 * @param {string} url Input url to which we concat markers
 * @param {array} markers Markers array (see getMarkers function)
 * @param {boolean} isHistorical If true, historical marker settings is used
 */
const addMarkersToUrl = (url, markers = [], isHistorical) => {
    let outputUrl = url
    let urlLength = url.length

    markers.every((marker) => {
        if (urlLength > 8000) return false // Break loop

        let markerSettings
        if (!isHistorical) markerSettings = `&markers=size:${marker.size}|color:${marker.color}|`
        else
            markerSettings = `&markers=size:${marker.size}|color:${marker.color}|scale:${marker.scale}|icon:${marker.icon}|`
        const markerSettingsLength = markerSettings.length

        if (urlLength + markerSettingsLength > 8000) return false // Break loop

        outputUrl = outputUrl.concat(markerSettings)
        urlLength += markerSettingsLength

        const markerLocations = marker.locations
        markerLocations.every((loc) => {
            // Location string is always 15 characters long (xx.xxxx,yy.yyyy) + pipe character (|) = 16
            if (urlLength + 16 < 8000) {
                outputUrl = outputUrl.concat(`${loc}|`)
                urlLength += 16
                return true // Go to next location (continue loop)
            }
            return false // Break loop
        })

        return true
    })

    return outputUrl
}

/**
 * Returns a static google maps url, which returns an image used by the <img> tag as source.
 * Map is set with these parameters:
 * Size = 640x640;
 * Scale = 2;
 * @param {array} addresses Array of all address objects to show on map
 * @param {object} activeAddress Object of the currently selected address
 * @param {boolean} activeZoom True - map is zoomed on the currently selected address
 * @param {boolean} showRegistered True - show registered addresses
 * @param {boolean} showBranch True - show branch addresse
 * @param {boolean} showShops True - show shops addresses
 */
export const getGoogleMapsStaticUrl = (
    addresses,
    activeAddress,
    activeZoom = false,
    showRegistered = true,
    showBranch = true,
    showShops = true,
    satelliteView = false
) => {
    // eslint-disable-line
    if (_.isEmpty(addresses) || _.isEmpty(activeAddress)) return null

    const baseUrl = 'https://maps.googleapis.com/maps/api/staticmap?'
    const size = '640x640'
    const scale = '2'
    const apiKey = config.googleMapsApiKey
    const style =
        '&style=feature:all|saturation:0|hue:0xe7ecf0&style=feature:road|saturation:-70&style=feature:transit|visibility:off&style=feature:poi|visibility:off&style=feature:water|visibility:simplified|saturation:-60' // eslint-disable-line
    const satelliteStyle = '&maptype=hybrid' // Satellite view with street names, markers, etc.
    const markers = getMarkers(addresses, activeAddress, false, showRegistered, showBranch, showShops)
    const historicalMarkers = getMarkers(addresses, activeAddress, true, showRegistered, showBranch, showShops)
    const activeAddressMarker = getActiveAddressMarker(activeAddress)

    let outputUrl = `${baseUrl}size=${size}&scale=${scale}${satelliteView ? satelliteStyle : style}`

    // If zoom on active address is active, return url with only active address.
    if (activeZoom || addresses.length === 1) {
        outputUrl = outputUrl.concat(`&${activeAddressMarker}`, '&zoom=15', `&key=${apiKey}`)
        return encodeURI(outputUrl)
    }

    outputUrl = outputUrl.concat(`&${activeAddressMarker}`) // Always add active address marker
    outputUrl = addMarkersToUrl(outputUrl, markers, false) // Add active markers
    outputUrl = addMarkersToUrl(outputUrl, historicalMarkers, true) // Add historical markers

    return encodeURI(outputUrl.concat(`&key=${apiKey}`))
}

/**
 * Return a static street view url, which returns an image used by the <img> tag as source.
 * Street view is set with these parameters:
 * Size = 640x640;
 * Radius = 300 meters;
 * Source = outdoors only;
 * @param {object} activeAddress Object of the currently selected address
 */
export const getStreetViewUrl = (activeAddress) => {
    if (_.isEmpty(activeAddress)) return null

    const baseUrl = 'https://maps.googleapis.com/maps/api/streetview?'
    const size = '640x640'
    const apiKey = config.googleMapsApiKeyView
    const radius = 300
    const source = 'outdoor'
    const lat = _.get(activeAddress, 'gpsX', null)
    const lng = _.get(activeAddress, 'gpsY', null)
    const address = _.get(activeAddress, 'value', null)

    return encodeURI(
        `${baseUrl}size=${size}&radius=${radius}&location=${
            lat && lng ? `${lat},${lng}` : `${address}`
        }&source=${source}&key=${apiKey}`
    ) // eslint-disable-line
}
